import React from "react";
import { useTranslation } from "react-i18next";
import argocdImg from "../assets/images/ARGOCDIMAGES.png";
import "../assets/styles/TerraformArticle.scss";

function ArgoCDArticle() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  if (isEn) {
    return (
      <div className="terraform-article">
        <h1>GitOps with ArgoCD on Kubernetes</h1>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
          Infrastructure · Kubernetes · GitOps — April 2025
        </p>

        <img src={argocdImg} alt="ArgoCD Dashboard" />

        {/* ── Context ─────────────────────────────── */}
        <h2>Context and Objective</h2>
        <p>
          Building on my Azure infrastructure project with Terraform, I wanted to set up a Git-driven continuous deployment pipeline. The goal: never apply changes manually to the cluster — every change goes through a merge request, is tracked in Git, and deployed automatically.
        </p>
        <p>
          I chose <strong>ArgoCD</strong> as the GitOps tool and <strong>MicroK8s</strong> as
          a lightweight Kubernetes distribution, running on an Azure Spot VM provisioned via Terraform.
        </p>

        {/* ── Architecture ─────────────────────────── */}
        <h2>Global Architecture</h2>
        <p>The stack relies on three main components:</p>
        <ul>
          <li><strong>GitLab</strong> — source repository for Kubernetes manifests and Helm Charts</li>
          <li><strong>MicroK8s</strong> — single-node Kubernetes cluster on Azure VM</li>
          <li><strong>ArgoCD</strong> — GitOps controller deployed in the cluster, watching the repository and applying changes</li>
        </ul>
        <p>
          The principle is simple: ArgoCD continuously watches the <code>main</code> branch of the
          GitOps repository. As soon as a commit is pushed, ArgoCD detects the gap between the desired state
          (Git) and the actual state (cluster), then synchronizes automatically.
        </p>

        {/* ── Installing ArgoCD ───────────────────── */}
        <h2>Installing ArgoCD on MicroK8s</h2>
        <p>
          ArgoCD is installed in its own namespace. The interface is then exposed via port-forward or an Ingress.
        </p>
        <pre><code>{`# Create the ArgoCD namespace
kubectl create namespace argocd

# Install ArgoCD
kubectl apply -n argocd \\
  -f https://raw.githubusercontent.com/argoproj/argo-cd/stable/manifests/install.yaml

# Get the initial admin password
kubectl get secret argocd-initial-admin-secret \\
  -n argocd -o jsonpath="{.data.password}" | base64 -d

# Expose the interface (development)
kubectl port-forward svc/argocd-server -n argocd 8080:443`}</code></pre>

        {/* ── Repository Structure ─────────────────── */}
        <h2>GitOps Repository Structure</h2>
        <p>
          I opted for an environment-based structure, with Helm Charts in a dedicated directory
          and environment-specific values in overlays.
        </p>
        <pre><code>{`gitops-repo/
├── apps/
│   ├── base/
│   │   └── app-deployment.yaml
│   └── overlays/
│       ├── staging/
│       │   └── values.yaml
│       └── production/
│           └── values.yaml
├── charts/
│   └── my-app/
│       ├── Chart.yaml
│       ├── values.yaml
│       └── templates/
│           ├── deployment.yaml
│           ├── service.yaml
│           └── ingress.yaml
└── argocd/
    └── application.yaml`}</code></pre>

        {/* ── ArgoCD Application ───────────────────── */}
        <h2>Defining an ArgoCD Application</h2>
        <p>
          An <code>Application</code> ArgoCD is a Kubernetes manifest that declares the Git source
          and the destination in the cluster. It's the link between the repository and the target namespace.
        </p>
        <pre><code>{`apiVersion: argoproj.io/v1alpha1
kind: Application
metadata:
  name: my-app-production
  namespace: argocd
spec:
  project: default
  source:
    repoURL: https://gitlab.com/nadjial/gitops-repo.git
    targetRevision: main
    path: apps/overlays/production
    helm:
      valueFiles:
        - values.yaml
  destination:
    server: https://kubernetes.default.svc
    namespace: production
  syncPolicy:
    automated:
      prune: true       # removes resources deleted from Git
      selfHeal: true    # corrects any manual drift
    syncOptions:
      - CreateNamespace=true`}</code></pre>
        <p>
          The key is <code>syncPolicy.automated</code>: with <code>prune</code> and{" "}
          <code>selfHeal</code> enabled, ArgoCD guarantees that the cluster always exactly reflects
          what is in Git — even if someone manually modifies a resource.
        </p>

        {/* ── CI/CD Pipeline ───────────────────────── */}
        <h2>Integration with GitLab CI</h2>
        <p>
          The GitLab CI pipeline builds the Docker image, pushes it to the registry, then updates
          the image tag in the GitOps repository. ArgoCD detects this commit and triggers synchronization.
        </p>
        <pre><code>{`# .gitlab-ci.yml (excerpt)
deploy:
  stage: deploy
  script:
    - docker build -t registry.gitlab.com/nadjial/my-app:$CI_COMMIT_SHA .
    - docker push registry.gitlab.com/nadjial/my-app:$CI_COMMIT_SHA
    # Update the tag in the GitOps repository
    - git clone https://gitlab.com/nadjial/gitops-repo.git
    - cd gitops-repo
    - sed -i "s|tag:.*|tag: $CI_COMMIT_SHA|" apps/overlays/production/values.yaml
    - git commit -am "chore: bump image tag to $CI_COMMIT_SHA"
    - git push`}</code></pre>

        {/* ── Rollback ─────────────────────────────── */}
        <h2>One-click Rollback</h2>
        <p>
          One of the great advantages of GitOps: rollback is a simple <code>git revert</code>.
          ArgoCD detects the rollback commit and restores the previous state automatically.
          From the ArgoCD interface, it's also possible to select any past revision and resynchronize it.
        </p>
        <pre><code>{`# Rollback via Git
git revert HEAD
git push

# Or directly via the ArgoCD CLI
argocd app rollback my-app-production --revision 42`}</code></pre>

        {/* ── Results ────────────────────────────── */}
        <h2>Results and Benefits</h2>
        <ul>
          <li>Zero manual deployments — everything goes through Git and is auditable</li>
          <li>Automatic detection of configuration drift</li>
          <li>Rollback in under 2 minutes via <code>git revert</code></li>
          <li>Full visibility into cluster state from the ArgoCD interface</li>
          <li>Staging and production environments managed from the same repository with separate values</li>
        </ul>

        {/* ── Conclusion ───────────────────────────── */}
        <h2>Conclusion</h2>
        <p>
          ArgoCD has completely transformed how I manage Kubernetes deployments. The combination
          of GitLab CI (build &amp; push) + ArgoCD (sync &amp; reconcile) gives a robust, traceable
          and easy-to-operate pipeline. The next step will be to integrate <strong>Helm Secrets</strong> to
          manage encrypted secrets directly in Git, and to explore <strong>ApplicationSets</strong>
          for multi-cluster management.
        </p>
      </div>
    );
  }

  return (
    <div className="terraform-article">
      <h1>GitOps avec ArgoCD sur Kubernetes</h1>
      <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
        Infrastructure · Kubernetes · GitOps — Avril 2025
      </p>

      <img src={argocdImg} alt="ArgoCD Dashboard" />

      {/* ── Contexte ─────────────────────────────── */}
      <h2>Contexte et objectif</h2>
      <p>
        Dans la continuité de mon projet d'infrastructure sur Azure avec Terraform, j'ai souhaité
        mettre en place une chaîne de déploiement continu pilotée par Git. L'objectif : ne plus
        jamais appliquer de changement manuellement sur le cluster — chaque modification passe
        par une merge request, est tracée dans Git, et déployée automatiquement.
      </p>
      <p>
        J'ai choisi <strong>ArgoCD</strong> comme outil GitOps et <strong>MicroK8s</strong> comme
        distribution Kubernetes légère, tournant sur une VM Spot Azure provisionnée via Terraform.
      </p>

      {/* ── Architecture ─────────────────────────── */}
      <h2>Architecture globale</h2>
      <p>La stack repose sur trois composants principaux :</p>
      <ul>
        <li><strong>GitLab</strong> — dépôt source pour les manifests Kubernetes et Charts Helm</li>
        <li><strong>MicroK8s</strong> — cluster Kubernetes single-node sur VM Azure</li>
        <li><strong>ArgoCD</strong> — contrôleur GitOps déployé dans le cluster, qui surveille le dépôt et applique les changements</li>
      </ul>
      <p>
        Le principe est simple : ArgoCD surveille en permanence la branche <code>main</code> du
        dépôt GitOps. Dès qu'un commit est poussé, ArgoCD détecte l'écart entre l'état désiré
        (Git) et l'état réel (cluster), puis synchronise automatiquement.
      </p>

      {/* ── Installation ArgoCD ───────────────────── */}
      <h2>Installation d'ArgoCD sur MicroK8s</h2>
      <p>
        ArgoCD s'installe dans son propre namespace. On expose ensuite l'interface via un
        port-forward ou un Ingress.
      </p>
      <pre><code>{`# Créer le namespace ArgoCD
kubectl create namespace argocd

# Installer ArgoCD
kubectl apply -n argocd \\
  -f https://raw.githubusercontent.com/argoproj/argo-cd/stable/manifests/install.yaml

# Récupérer le mot de passe admin initial
kubectl get secret argocd-initial-admin-secret \\
  -n argocd -o jsonpath="{.data.password}" | base64 -d

# Exposer l'interface (développement)
kubectl port-forward svc/argocd-server -n argocd 8080:443`}</code></pre>

      {/* ── Structure du dépôt ───────────────────── */}
      <h2>Structure du dépôt GitOps</h2>
      <p>
        J'ai opté pour une structure par environnement, avec les Charts Helm dans un répertoire
        dédié et les valeurs spécifiques par env dans des overlays.
      </p>
      <pre><code>{`gitops-repo/
├── apps/
│   ├── base/
│   │   └── app-deployment.yaml
│   └── overlays/
│       ├── staging/
│       │   └── values.yaml
│       └── production/
│           └── values.yaml
├── charts/
│   └── my-app/
│       ├── Chart.yaml
│       ├── values.yaml
│       └── templates/
│           ├── deployment.yaml
│           ├── service.yaml
│           └── ingress.yaml
└── argocd/
    └── application.yaml`}</code></pre>

      {/* ── Application ArgoCD ───────────────────── */}
      <h2>Définition d'une Application ArgoCD</h2>
      <p>
        Une <code>Application</code> ArgoCD est un manifest Kubernetes qui déclare la source Git
        et la destination dans le cluster. C'est le lien entre le dépôt et le namespace cible.
      </p>
      <pre><code>{`apiVersion: argoproj.io/v1alpha1
kind: Application
metadata:
  name: my-app-production
  namespace: argocd
spec:
  project: default
  source:
    repoURL: https://gitlab.com/nadjial/gitops-repo.git
    targetRevision: main
    path: apps/overlays/production
    helm:
      valueFiles:
        - values.yaml
  destination:
    server: https://kubernetes.default.svc
    namespace: production
  syncPolicy:
    automated:
      prune: true       # supprime les ressources retirées de Git
      selfHeal: true    # corrige toute dérive manuelle
    syncOptions:
      - CreateNamespace=true`}</code></pre>
      <p>
        La clé est <code>syncPolicy.automated</code> : avec <code>prune</code> et{" "}
        <code>selfHeal</code> activés, ArgoCD garantit que le cluster reflète toujours
        exactement ce qui est dans Git — même si quelqu'un modifie une ressource à la main.
      </p>

      {/* ── Pipeline CI/CD ───────────────────────── */}
      <h2>Intégration avec GitLab CI</h2>
      <p>
        Le pipeline GitLab CI construit l'image Docker, la pousse sur le registry, puis met à jour
        le tag d'image dans le dépôt GitOps. ArgoCD détecte ce commit et déclenche la synchronisation.
      </p>
      <pre><code>{`# .gitlab-ci.yml (extrait)
deploy:
  stage: deploy
  script:
    - docker build -t registry.gitlab.com/nadjial/my-app:$CI_COMMIT_SHA .
    - docker push registry.gitlab.com/nadjial/my-app:$CI_COMMIT_SHA
    # Mise à jour du tag dans le dépôt GitOps
    - git clone https://gitlab.com/nadjial/gitops-repo.git
    - cd gitops-repo
    - sed -i "s|tag:.*|tag: $CI_COMMIT_SHA|" apps/overlays/production/values.yaml
    - git commit -am "chore: bump image tag to $CI_COMMIT_SHA"
    - git push`}</code></pre>

      {/* ── Rollback ─────────────────────────────── */}
      <h2>Rollback en un clic</h2>
      <p>
        L'un des grands avantages de GitOps : le rollback est un simple <code>git revert</code>.
        ArgoCD détecte le commit de rollback et restaure l'état précédent automatiquement.
        Depuis l'interface ArgoCD, il est aussi possible de sélectionner n'importe quelle révision
        passée et de la resynchroniser.
      </p>
      <pre><code>{`# Rollback via Git
git revert HEAD
git push

# Ou directement via la CLI ArgoCD
argocd app rollback my-app-production --revision 42`}</code></pre>

      {/* ── Résultats ────────────────────────────── */}
      <h2>Résultats et bénéfices</h2>
      <ul>
        <li>Zéro déploiement manuel — tout passe par Git et est auditable</li>
        <li>Détection automatique des dérives de configuration</li>
        <li>Rollback en moins de 2 minutes via <code>git revert</code></li>
        <li>Visibilité complète sur l'état du cluster depuis l'interface ArgoCD</li>
        <li>Environnements staging et production gérés depuis le même dépôt avec des valeurs séparées</li>
      </ul>

      {/* ── Conclusion ───────────────────────────── */}
      <h2>Conclusion</h2>
      <p>
        ArgoCD a complètement transformé ma façon de gérer les déploiements Kubernetes. La combinaison
        GitLab CI (build &amp; push) + ArgoCD (sync &amp; reconcile) donne une chaîne robuste, traçable
        et facile à opérer. La prochaine étape sera d'intégrer <strong>Helm Secrets</strong> pour
        gérer les secrets chiffrés directement dans Git, et d'explorer <strong>ApplicationSets</strong>
        pour la gestion multi-clusters.
      </p>
    </div>
  );
}

export default ArgoCDArticle;
